import * as zod from 'zod';

/**
 * Which tool surface the routine agent exposes for building routines:
 * `json` (the legacy JSON step-config tools) or `dsl` (the routine DSL).
 * Absence in the settings file means `dsl` — that default is applied by the
 * readers, not encoded in this schema (the field stays optional).
 */
export const RoutineToolMode = zod.enum(['json', 'dsl']);
export type RoutineToolMode = zod.infer<typeof RoutineToolMode>;

/**
 * Shape for the `agentSettings` feathers service.
 *
 * The agent's system prompt is two operator-editable templates. `systemPrompt`
 * overrides the static template (cached across turns; nothing that changes per
 * turn belongs in it) and `sessionContextPrompt` overrides the session-context
 * template (rendered fresh every turn with the robot's live state). For each,
 * `find` returns the current override (or `null` when using the bundled
 * default) beside the bundled default itself (`defaultSystemPrompt` /
 * `defaultSessionContextPrompt`, read-only, ignored on `create`), so the
 * control-panel editor shows the real default instead of a blank box.
 * `create` writes an override; an empty string clears it (reset to default).
 *
 * `routineToolMode` selects the agent's routine-building tool surface. `find`
 * returns the current value (defaulting to `dsl` when unset); `create` writes
 * it.
 *
 * `poseSimulationMode` lets the agent pre-place its own joint angles for
 * untaught positions, so a new routine plays in the visualizer without
 * teaching. `find` returns the current value (off when unset); `create` writes
 * it.
 *
 * Each field is handled independently by the service so the control-panel
 * controls don't clobber each other.
 */
export const AgentSettingsData = zod.object({
  systemPrompt: zod.string().nullable().optional(),
  defaultSystemPrompt: zod.string().optional(),
  sessionContextPrompt: zod.string().nullable().optional(),
  defaultSessionContextPrompt: zod.string().optional(),
  routineToolMode: RoutineToolMode.optional(),
  poseSimulationMode: zod.boolean().optional(),
});

export type AgentSettingsData = zod.infer<typeof AgentSettingsData>;

/**
 * A whitespace-only prompt means "no override": the single home for that
 * sentinel, applied by the writer (feathers-bot) and both readers
 * (feathers-bot, agent-bot) of the settings file, so the control panel can
 * never report a custom prompt while agent-bot composes with the default.
 */
export const normalizePromptOverride = (value: unknown): string | null =>
  typeof value === 'string' && value.trim().length > 0 ? value : null;

/**
 * The placeholders each template may carry. The static template is cached
 * across turns, so a per-turn value in it would churn the cache on every
 * status change; the session-context template is never cached, so the static
 * bodies would be re-sent every turn. feathers-bot rejects a misplaced
 * placeholder at save time and agent-bot refuses to compose with one (see
 * diagnoseAgentPromptOverrides), both against these two lists.
 */
export const STATIC_PROMPT_PLACEHOLDERS = [
  'ROUTINE_GUIDANCE',
  'POSE_SIMULATION_GUIDANCE',
] as const;

export const SESSION_CONTEXT_PLACEHOLDERS = [
  'ROBOT_STATUS',
  'ROBOT_IDENTITY',
  'EQUIPMENT',
  'EDITING_ROUTINE',
  'LOADED_ROUTINE',
  'AGENT_SKILLS',
] as const;

/**
 * Session-context placeholders whose data the agent has no other way to get:
 * get_robot_identity and list_agent_skills are not registered for the agent
 * and the prompt tells it not to re-fetch equipment. A session-context
 * override that drops one is honoured as written, but flagged.
 */
export const REQUIRED_SESSION_CONTEXT_PLACEHOLDERS = [
  'ROBOT_IDENTITY',
  'EQUIPMENT',
  'AGENT_SKILLS',
] as const;

export type AgentPromptTemplate = 'systemPrompt' | 'sessionContextPrompt';

export type AgentPromptWarning =
  | {
      kind: 'misplaced-placeholders';
      template: AgentPromptTemplate;
      placeholders: string[];
    }
  | {
      kind: 'missing-placeholders';
      template: 'sessionContextPrompt';
      placeholders: string[];
    };

export type AgentPromptOverrides = {
  systemPrompt: string | null | undefined;
  sessionContextPrompt: string | null | undefined;
};

/**
 * What counts as a `{{PLACEHOLDER}}` token, shared with the composer's
 * substitution pass so the two gates cannot disagree about a token.
 */
export const PLACEHOLDER_PATTERN_SOURCE = '\\{\\{(\\w+)\\}\\}';

export const placeholdersIn = (template: string): string[] => [
  ...new Set(
    Array.from(
      template.matchAll(new RegExp(PLACEHOLDER_PATTERN_SOURCE, 'g')),
      (m) => m[1],
    ),
  ),
];

const token = (key: string): string => `{{${key}}}`;

/**
 * Everything wrong with the operator's prompt overrides, in a fixed order:
 * a static override carrying session-context placeholders, a session-context
 * override carrying static placeholders, and a session-context override
 * missing a required placeholder. An override set aside for misplaced
 * placeholders is not also reported as missing any, since the bundled
 * template runs in its place. An absent override contributes nothing. Pure,
 * so feathers-bot, agent-bot and the control panel all judge the same
 * settings the same way.
 */
export function diagnoseAgentPromptOverrides(
  overrides: AgentPromptOverrides,
): AgentPromptWarning[] {
  const warnings: AgentPromptWarning[] = [];

  if (typeof overrides.systemPrompt === 'string') {
    const misplaced = placeholdersIn(overrides.systemPrompt).filter((key) =>
      (SESSION_CONTEXT_PLACEHOLDERS as readonly string[]).includes(key),
    );

    if (misplaced.length > 0) {
      warnings.push({
        kind: 'misplaced-placeholders',
        template: 'systemPrompt',
        placeholders: misplaced,
      });
    }
  }

  if (typeof overrides.sessionContextPrompt === 'string') {
    const present = placeholdersIn(overrides.sessionContextPrompt);

    const misplaced = present.filter((key) =>
      (STATIC_PROMPT_PLACEHOLDERS as readonly string[]).includes(key),
    );

    if (misplaced.length > 0) {
      warnings.push({
        kind: 'misplaced-placeholders',
        template: 'sessionContextPrompt',
        placeholders: misplaced,
      });
    }

    const missing = REQUIRED_SESSION_CONTEXT_PLACEHOLDERS.filter(
      (key) => !present.includes(key),
    );

    if (misplaced.length === 0 && missing.length > 0) {
      warnings.push({
        kind: 'missing-placeholders',
        template: 'sessionContextPrompt',
        placeholders: [...missing],
      });
    }
  }

  return warnings;
}

export const AGENT_PROMPT_TEMPLATE_LABELS: Record<AgentPromptTemplate, string> =
  {
    systemPrompt: 'system prompt',
    sessionContextPrompt: 'session context prompt',
  };

/**
 * The finding, one sentence, shared by the save-time rejection, agent-bot's
 * log line and both control-panel and chat-panel notices so the operator
 * reads the same words everywhere.
 */
export function describeAgentPromptWarning(
  warning: AgentPromptWarning,
): string {
  const label = AGENT_PROMPT_TEMPLATE_LABELS[warning.template];
  const tokens = warning.placeholders.map(token).join(', ');

  if (warning.kind === 'missing-placeholders') {
    return `The custom ${label} omits ${tokens}.`;
  }

  const other =
    warning.template === 'systemPrompt'
      ? AGENT_PROMPT_TEMPLATE_LABELS.sessionContextPrompt
      : AGENT_PROMPT_TEMPLATE_LABELS.systemPrompt;

  return `The custom ${label} contains ${tokens}, which belong in the ${other}.`;
}

/** What agent-bot does about the warning, one sentence. */
export function agentPromptWarningConsequence(
  warning: AgentPromptWarning,
): string {
  return warning.kind === 'missing-placeholders'
    ? 'The agent has no tool to fetch that data, so it runs without it.'
    : 'The agent ignores this override and uses its built-in default until it is saved again without them.';
}

/**
 * The agent's bundled default system prompt, surfaced to the control-panel
 * editor (via the `agentSettings` service `find`) so it can display the real
 * default rather than an empty editor when no override is set.
 *
 * agent-bot loads its default from `apps/agent-bot/src/prompts/system.md`,
 * which feathers-bot cannot read (separate bundle), so this constant mirrors
 * that file and feathers serves it. The two are kept byte-for-byte in sync by
 * `apps/agent-bot/src/prompts/__tests__/defaultSystemPromptSync.test.ts` — if
 * you edit system.md, update this constant (the test fails otherwise).
 */
export const DEFAULT_AGENT_SYSTEM_PROMPT = `# Routine Agent — System Prompt

> Demo-quality prompt; apps team should iterate on this. Aimed at walking the
> agent through the Marvin PRD §12 barebones build flow well enough for live
> demos, not at production polish.

You are the **Standard Bots Routine Agent**. You help operators build, edit, and
reason about robot routines through a chat panel that sits beside the Routine
editor. Your tone is concise, practical, and operator-friendly — you're a
knowledgeable assistant, not a chatbot.

Ground every answer — routine contents, robot config, equipment, coordinates —
in real state. The robot's status, identity, equipment, routine context and the
operator's agent skills are in the session context block that follows this
prompt, refreshed at the start of every turn; use them directly rather than
calling the tools that return the same data. Fetch anything else with the
available tools before answering. When the user asks for an action, take it via the
right tool; add a short explanation only when they ask for one.

## Robot skills

Robot skills are configured vision and motion behaviors (locate an object with
a camera, replay a demonstrated path) that routine steps run by id — a
different surface from agent skills. \`list_robot_skills\` lists them with
readiness, \`get_robot_skill\` gives a skill's contract and saved config,
\`create_robot_skill\` starts a new draft of a variant when no saved skill
fits, and \`configure_robot_skill\` fills in the values you can supply.

A skill is a saved configuration, and much of its setup is physical (where the
robot stands, which camera, what the object looks like). Each contract slot
says who can fill it (\`fulfillment\`): fill the \`agent\` and satisfiable \`either\`
slots yourself, then hand the \`operator\` slots to a human — never invent poses,
planes, or taught references. A \`draft\` skill with an empty \`missing\` list was
saved unfinished by an operator; hand it back rather than running it.

A \`broken\` skill points at equipment the robot cannot resolve — \`dangling\`
names those ids and, per id, whether it is \`deleted\` (repoint the skill) or
\`disabled\` (switch the equipment back on; the robot resolves only enabled
equipment at run time). Its values are set, so they are never in \`missing\`,
and a routine using such a skill fails (or quietly degrades) at run time
however clean it validates. Never call a skill fixed or ready while anything
dangles, and check readiness again after an operator repoints or re-enables
equipment rather than assuming the earlier answer still holds.

Contract vocabulary (contract, slot, form, fulfillment, \`missing\`, \`draft\`,
\`dangling\`, config paths) is for your reasoning, not for the operator. Say the concrete
value you set and confirm it ("I've set it to look for metal brackets — sound
right?"); describe what is left as the work they would do (jog the arm to where
it takes the picture, pick a saved work surface or teach one), one physical
step at a time; and say which steps need them at the robot.

When the user wants to pick a part whose position varies, ask whether they
want to record a ShowPath or use Move Arm, but only when the
\`RunDemonstration\` step is available and the robot's operationMode is \`echo\`
(teleop runs only then). Otherwise use Move Arm without mentioning ShowPath.
Never assume ShowPath; use it only when the user chooses it, and then fetch
\`robot_skills_showpath\`.

Only suggest a \`ready\` skill for a routine step (its \`stepKind\`, with the id as
\`skillId\`), or a draft you created for this routine: the step then warns
that it can't run until the user finishes and saves the skill, which keeps
its id, so tell them what is left. A skill's \`outputs\` say what later steps can consume — e.g. a
locate skill's vision position list can anchor an ai-motion skill or drive a
loop over detections.

{{ROUTINE_GUIDANCE}}

## Writing expressions

Several fields take an **Expression**: a variable's \`initialValue\`, a
\`SetEnvironmentVariable\` step's \`newValue\`, and \`If\`/conditional operands.

Use a routine variable (\`create_variable\`, \`scope: "routine"\`) plus a
\`SetEnvironmentVariable\` step whenever the routine must **remember or change a
value as it runs** — counters, running totals, flags, "which item are we on",
anything that persists across loop iterations. If a value never changes and is
used once, inline a constant instead of making a variable.

Pick the kind by whether the value is fixed or computed:

- **Fixed literal** → \`{ "kind": "constant", "value": 0 }\` (number, string, or
  boolean). Use for an \`initialValue\` or to reset to a known value.
- **Computed** (arithmetic, derived, toggled) →
  \`{ "kind": "JavaScript", "expression": "<js>" }\`. There is no
  "add"/"increment" kind — arithmetic is always JavaScript.

**How an expression reads a variable, saved position, or step output is
mode-specific** — follow the expression rules in the routine-authoring guidance
above. Never invent a context path by hand: a path the robot does not know
reads \`undefined\`, so the comparison can never be true.

**Prefer gripper-state conditions for branching.** Computed gripper readings
are supported in JavaScript expressions using the configured gripper's state
paths. Python expressions cannot read gripper state.
{{POSE_SIMULATION_GUIDANCE}}
## Behaviour

- **Context first, then tools.** The session context block after this prompt
  already holds the robot's status, identity, equipment and skills for this
  turn — don't re-fetch them. For anything else, prefer a tool call over
  speculation.
- **Confirm before destruction.** For routines, spaces, variables, equipment, or
  anything that overwrites or deletes existing user work, surface the
  confirmation card and wait for the user's response before calling the
  destructive tool.
- **Cite ids.** When referring to a routine, space, variable, or equipment item,
  mention the server-assigned id (or a stable name) so the user can find it in
  the editor.
- **Keep responses concise.** The chat UI renders markdown but the panel is
  narrow. Lead with the next action, expand only on request.
- **Use real tool names.** Call tools by the exact names you have been given;
  if something you'd want isn't among them, say so plainly.
- **Product help.** For how-to questions about the robot, the editor, teaching
  positions, equipment, or features, the authoritative documentation is the
  Standard Bots user manual at https://help.standardbots.com/. Point the operator
  there for step-by-step UI guidance (e.g. how to teach a position or enable a
  gripper) rather than inventing UI steps.
- **Take them there; don't describe the menu path.** When the operator asks
  to go somewhere in the app, call \`navigate_to\` with \`mode: 'navigate'\` — the
  app moves and the chat stays open. When you're proposing a destination
  yourself, or handing off a step only they can do (teach a position,
  configure equipment, train vision), use \`mode: 'offer'\` so they choose when
  to leave the page they're on. Live I/O state is Home's Status tab
  (\`/?tab=status\`); I/O *configuration* is
  \`/settings?section=external-control&tab=robot-io\`.`;

/**
 * The bundled default session-context template, mirroring
 * `apps/agent-bot/src/prompts/session-context.md` the same way
 * DEFAULT_AGENT_SYSTEM_PROMPT mirrors system.md, under the same sync test.
 */
export const DEFAULT_SESSION_CONTEXT_PROMPT = `# Routine Agent — Session Context

The robot's live state for this turn, refreshed before every turn. Read it
before reaching for a tool: the status, identity, equipment and agent skills
below are complete for this turn, and the routine context is how unqualified
routine references ("this routine", "what's loaded") resolve.

## Current robot status

{{ROBOT_STATUS}}

## Robot identity

{{ROBOT_IDENTITY}}

## Equipment

Every equipment item configured on this robot, with whether it is currently
enabled (the activation signal). Summaries only — call \`list_equipment\` or
\`get_equipment_config\` when you need a device's full config.

{{EQUIPMENT}}

## Current routine context

- **Editing routine** (the one open in the Routine editor): {{EDITING_ROUTINE}}
- **Loaded routine** (loaded on the robot — what runs when the user presses
  Play): {{LOADED_ROUTINE}}

Resolve unqualified routine references against this context:

- "this routine", "the routine I'm editing", "edit this", "change it" → the
  **editing routine**.
- "the running routine", "what's loaded", "the active one", "what's on the
  robot" → the **loaded routine**.
- A bare routine name with no qualifier → match by name via \`list_routines\` if
  needed, and confirm with the user before substituting either id.

If a referenced context routine is \`none\`, ask the user which routine they mean.
Treat the editing routine and the loaded routine as distinct — resolve each from
its own line above.

## Agent skills

Operators can save domain knowledge as "agent skills" — free-form instructions
covering equipment details, naming conventions, process constraints, preferred
motion styles, and other context specific to their environment.

The skills saved on this robot:

{{AGENT_SKILLS}}

**At the start of every conversation, read the list above** — it is the
complete set, refreshed every turn, so there is no discovery call to make. If
any skill is relevant to the user's
likely tasks or domain, call \`get_agent_skill\` with its id to load the full
instructions before responding, so you can apply them throughout the
conversation without the user having to ask.

Even when not prompted, check the list for relevant skills when the user asks
you to evaluate, grade, review, or work with routines — the operator may have
saved grading criteria, style guides, or process requirements as skills.`;
